import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import Spinner from 'components/ui/Spinner';
import classNames from 'classnames';
import EditableSection from 'components/v2/EditableSection';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import FormGroup from 'components/v2/FormGroup';
import {
  getUserBillReadyEvents,
  upsertUserBillReadyEvents,
} from 'utils/UserServiceAPIUtils';

const SHIPMENT_MOVEMENT_EVENT_TYPES = [
  'import_drayage_event',
  'import_rail_event',
  'domestic_delivery_drayage_event',
  'domestic_delivery_rail_event',
  'export_drayage_event',
  'export_rail_event',
  'domestic_pickup_drayage_event',
  'domestic_pickup_rail_event',
  'reposition_drayage_event',
  'reposition_rail_event',
];

const FULL_CONTAINER_DELIVERED = 'FULL_CONTAINER_DELIVERED';
const EMPTY_CONTAINER_RETURNED = 'EMPTY_CONTAINER_RETURNED';
const FULL_CONTAINER_PICKED_UP_LOADED = 'FULL_CONTAINER_PICKED_UP_LOADED';
const PICKUP_LOADED_OR_EMPTY = 'PICKUP_LOADED_OR_EMPTY';
const DELIVERY_LOADED_OR_EMPTY = 'DELIVERY_LOADED_OR_EMPTY';
const IMPORT_OR_DOMESTIC_DELIVERY_SLUGS = [
  FULL_CONTAINER_DELIVERED,
  EMPTY_CONTAINER_RETURNED,
];
const EXPORT_OR_DOMESTIC_PICKUP_SLUGS = [
  FULL_CONTAINER_PICKED_UP_LOADED,
  FULL_CONTAINER_DELIVERED,
];
const REPOSITION_SLUGS = [PICKUP_LOADED_OR_EMPTY, DELIVERY_LOADED_OR_EMPTY];

function BillReadyEventSettingsSummary({ billReadyEventData }) {
  const {
    import_drayage_event,
    import_rail_event,
    export_drayage_event,
    export_rail_event,
    domestic_delivery_drayage_event,
    domestic_delivery_rail_event,
    domestic_pickup_drayage_event,
    domestic_pickup_rail_event,
    reposition_drayage_event,
    reposition_rail_event,
  } = billReadyEventData || {};

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);

  function renderSpinner() {
    return (
      <div className="grid grid-cols-2 gap-4">
        <Spinner showMessage message={t('LOADING')} />
      </div>
    );
  }

  useEffect(() => {
    if (Object.keys(billReadyEventData).length) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [billReadyEventData]);

  return isLoading ? (
    renderSpinner()
  ) : (
    <div className="grid grid-cols-2 gap-4">
      <DescriptionListItem label={t('IMPORT_DRAYAGE')} className="">
        {t(import_drayage_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('IMPORT_RAIL')} className="">
        {t(import_rail_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('DOMESTIC_DELIVERY_DRAYAGE')} className="">
        {t(domestic_delivery_drayage_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('DOMESTIC_DELIVERY_RAIL')} className="">
        {t(domestic_delivery_rail_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('EXPORT_DRAYAGE')} className="">
        {t(export_drayage_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('EXPORT_RAIL')} className="">
        {t(export_rail_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('DOMESTIC_PICKUP_DRAYAGE')} className="">
        {t(domestic_pickup_drayage_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('DOMESTIC_PICKUP_RAIL')} className="">
        {t(domestic_pickup_rail_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('REPOSITION_DELIVERY_DRAYAGE')} className="">
        {t(reposition_drayage_event?.toUpperCase())}
      </DescriptionListItem>
      <DescriptionListItem label={t('REPOSITION_DELIVERY_RAIL')} className="">
        {t(reposition_rail_event?.toUpperCase())}
      </DescriptionListItem>
    </div>
  );
}

function BillReadyEventSettingsForm({
  billReadyEventData,
  userId,
  saving,
  onSaveSuccess,
  onSaveFail,
}) {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({});
  const slugsToOptions = (slugs) =>
    slugs.map((slug) => ({ name: t(slug), value: slug }));

  function handleSettingFormValues() {
    const initialData = {};
    SHIPMENT_MOVEMENT_EVENT_TYPES.forEach((shipmentMovementType) => {
      const subData = {
        name: t(billReadyEventData[shipmentMovementType]),
        value: billReadyEventData[shipmentMovementType],
      };
      initialData[shipmentMovementType] = subData;
    });
    setFormValues(initialData);
  }

  useEffect(() => {
    handleSettingFormValues();
  }, []);

  useEffect(() => {
    if (saving) {
      saveForm();
    }
  }, [saving]);

  async function saveForm() {
    try {
      const newUserBillReadyEventsSetting = {
        import_drayage_event: formValues?.import_drayage_event?.value,
        import_rail_event: formValues?.import_rail_event?.value,
        domestic_delivery_drayage_event:
          formValues?.domestic_delivery_drayage_event?.value,
        domestic_delivery_rail_event:
          formValues?.domestic_delivery_rail_event?.value,
        export_drayage_event: formValues?.export_drayage_event?.value,
        export_rail_event: formValues?.export_rail_event?.value,
        domestic_pickup_drayage_event:
          formValues?.domestic_pickup_drayage_event?.value,
        domestic_pickup_rail_event:
          formValues?.domestic_pickup_rail_event?.value,
        reposition_drayage_event: formValues?.reposition_drayage_event?.value,
        reposition_rail_event: formValues?.reposition_rail_event?.value,
      };

      const result = await upsertUserBillReadyEvents(
        userId,
        newUserBillReadyEventsSetting,
      );

      const success = result?.data?.upsertBillReadyEventsSetting?.success;
      if (!success) throw new Error('Setting was not successfully updated');

      showToast(t('EDIT_BILL_READY_EVENTS_SETTING_SUCCESS'));
      onSaveSuccess();
    } catch (err) {
      console.error('Error during upsertUserBillReadyEvents: ', err.message);
      showToast(t('EDIT_BILL_READY_EVENTS_SETTING_FAILURE'), null, 'error');
      onSaveFail();
    }
  }

  function handleChange(field) {
    return function (value) {
      setFormValues((data) => ({
        ...data,
        [field]: value,
      }));
    };
  }
  return (
    <div className="grid grid-cols-2 gap-4">
      <FormGroup label={t('IMPORT_DRAYAGE')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(IMPORT_OR_DOMESTIC_DELIVERY_SLUGS)}
          value={formValues.import_drayage_event}
          onChange={handleChange('import_drayage_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('IMPORT_RAIL')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(IMPORT_OR_DOMESTIC_DELIVERY_SLUGS)}
          value={formValues.import_rail_event}
          onChange={handleChange('import_rail_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('DOMESTIC_DELIVERY_DRAYAGE')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(IMPORT_OR_DOMESTIC_DELIVERY_SLUGS)}
          value={formValues.domestic_delivery_drayage_event}
          onChange={handleChange('domestic_delivery_drayage_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('DOMESTIC_DELIVERY_RAIL')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(IMPORT_OR_DOMESTIC_DELIVERY_SLUGS)}
          value={formValues.domestic_delivery_rail_event}
          onChange={handleChange('domestic_delivery_rail_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('EXPORT_DRAYAGE')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(EXPORT_OR_DOMESTIC_PICKUP_SLUGS)}
          value={formValues.export_drayage_event}
          onChange={handleChange('export_drayage_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('EXPORT_RAIL')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(EXPORT_OR_DOMESTIC_PICKUP_SLUGS)}
          value={formValues.export_rail_event}
          onChange={handleChange('export_rail_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('DOMESTIC_PICKUP_DRAYAGE')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(EXPORT_OR_DOMESTIC_PICKUP_SLUGS)}
          value={formValues.domestic_pickup_drayage_event}
          onChange={handleChange('domestic_pickup_drayage_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('DOMESTIC_PICKUP_RAIL')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(EXPORT_OR_DOMESTIC_PICKUP_SLUGS)}
          value={formValues.domestic_pickup_rail_event}
          onChange={handleChange('domestic_pickup_rail_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('REPOSITION_DELIVERY_DRAYAGE')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(REPOSITION_SLUGS)}
          value={formValues.reposition_drayage_event}
          onChange={handleChange('reposition_drayage_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
      <FormGroup label={t('REPOSITION_DELIVERY_RAIL')} className="">
        <CustomDropdown
          label=""
          options={slugsToOptions(REPOSITION_SLUGS)}
          value={formValues.reposition_rail_event}
          onChange={handleChange('reposition_rail_event')}
          nameKey="name"
          valueKey="value"
        />
      </FormGroup>
    </div>
  );
}

export default function BillReadyEventSettings({ userId, className }) {
  const { t } = useTranslation();
  const [userBillReadyEventsSetting, setUserBillReadyEventsSetting] = useState(
    {},
  );
  const [canEdit, setCanEdit] = useState(false);
  const isShipperOnboarder = SessionStore.hasShipperOnboardingRole();

  const fetchUserBillReadyEventsSetting = async () => {
    try {
      const result = await getUserBillReadyEvents(userId);
      const { user_id, ...billReadyEvents } =
        result?.data?.getBillReadyEventsSetting || {};
      if (!billReadyEvents) throw new Error('Missing Bill Ready Events data');
      setUserBillReadyEventsSetting(billReadyEvents);
      setCanEdit(true);
    } catch (e) {
      console.error('Error fetching getUserBillReadyEvents', e.message);
    }
  };

  useEffect(() => {
    fetchUserBillReadyEventsSetting();
  }, []);

  const handleAfterSave = (onSaveSuccess) => {
    return () => {
      fetchUserBillReadyEventsSetting();
      onSaveSuccess();
    };
  };

  return (
    <EditableSection
      title={t('DRAYAGE_BILL_READY_EVENTS')}
      className={classNames(className)}
      canEdit={canEdit && isShipperOnboarder}
    >
      {(editing, saving, onSaveSuccess, onSaveFail) => (
        <>
          {!editing && (
            <BillReadyEventSettingsSummary
              billReadyEventData={userBillReadyEventsSetting}
            />
          )}
          {editing && (
            <BillReadyEventSettingsForm
              saving={saving}
              billReadyEventData={userBillReadyEventsSetting}
              userId={userId}
              onSaveSuccess={handleAfterSave(onSaveSuccess)}
              onSaveFail={onSaveFail}
            />
          )}
        </>
      )}
    </EditableSection>
  );
}
